import React from "react";
import { Box, Button, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { History as HistoryIcon, PersonAdd } from "@mui/icons-material";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { Customer } from "../../../types";
import { CustomerSearchAutocomplete } from "../../common/CustomerSearchAutocomplete";
import { CustomerPhoneChecks } from "../../orders/DeliveryRecordCheck";
import { getCustomerFullName } from "../../../utils/customerUtils";
import { NewCustomerFields } from "./NewCustomerFields";
import { OrderFormData } from "./orderFormSchema";

interface Props {
    mode: "create" | "edit";
    customerMode: "select" | "create";
    control: Control<OrderFormData>;
    errors: FieldErrors<OrderFormData>;
    selectedCustomer: Customer | null;
    onCustomerPicked: (customer: Customer | null) => void;
    /** Switch to a new customer, carrying over what was typed in the search. */
    onStartNewCustomer: (typed: string) => void;
    onPickExisting: () => void;
    onOpenHistory: () => void;
    /** Open the new customer's "More details" (a prefill filled them). */
    newCustomerDetailsOpen: boolean;
}

/**
 * Who the order is for: one search by name or phone. No match offers a new
 * customer with what was typed; a new customer needs only a name.
 */
export const CustomerSection: React.FC<Props> = ({
    mode,
    customerMode,
    control,
    errors,
    selectedCustomer,
    onCustomerPicked,
    onStartNewCustomer,
    onPickExisting,
    onOpenHistory,
    newCustomerDetailsOpen,
}) => {
    const searchText = React.useRef("");

    if (mode === "edit") {
        // The customer can't be changed here; show who it is (phone checks
        // only appear for customers who have a phone).
        return (
            <Box>
                <Typography variant="body2" fontWeight={600} color={selectedCustomer ? "text.primary" : "text.secondary"}>
                    {selectedCustomer
                        ? getCustomerFullName(selectedCustomer) || "Unnamed customer"
                        : "No customer on this order"}
                </Typography>
                <CustomerPhoneChecks customer={selectedCustomer} />
            </Box>
        );
    }

    if (customerMode === "create") {
        return (
            <Box>
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                    <Typography variant="body2" fontWeight={600}>
                        New customer
                    </Typography>
                    <Link component="button" type="button" variant="body2" onClick={onPickExisting}>
                        Pick an existing customer instead
                    </Link>
                </Box>
                <NewCustomerFields control={control} errors={errors} detailsOpen={newCustomerDetailsOpen} />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Controller
                        name="customer_id"
                        control={control}
                        render={() => (
                            <CustomerSearchAutocomplete
                                value={selectedCustomer}
                                onChange={(value) => onCustomerPicked(value)}
                                onCreateNew={onStartNewCustomer}
                                onSearchTextChange={(text) => {
                                    searchText.current = text;
                                }}
                                label="Customer — name or phone"
                                error={!!errors.customer_id}
                                helperText={errors.customer_id?.message}
                                autoHighlight
                                required
                            />
                        )}
                    />
                </Box>
                {selectedCustomer ? (
                    <Tooltip title="Past orders — add items from them">
                        <IconButton onClick={onOpenHistory} color="primary" sx={{ mt: 1 }} aria-label="Past orders">
                            <HistoryIcon />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Button
                        startIcon={<PersonAdd />}
                        onClick={() => onStartNewCustomer(searchText.current)}
                        sx={{ mt: 1, flexShrink: 0, whiteSpace: "nowrap" }}
                    >
                        New
                    </Button>
                )}
            </Box>
            <CustomerPhoneChecks customer={selectedCustomer} />
        </Box>
    );
};

export default CustomerSection;
